import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { OnboardingService, SubmitOnboardingRequest } from '../../services/onboarding';
import { OnboardingApplication, OnboardingForm, OnboardingResult } from '../../types';

// 异步actions
export const fetchOnboardingForm = createAsyncThunk(
  'onboarding/fetchForm',
  async (_, { rejectWithValue }) => {
    try {
      const response = await OnboardingService.getOnboardingForm();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取入驻表单失败');
    }
  }
);

export const submitApplication = createAsyncThunk(
  'onboarding/submitApplication',
  async (data: SubmitOnboardingRequest, { rejectWithValue }) => {
    try {
      const response = await OnboardingService.submitApplication(data);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '提交申请失败');
    }
  }
);

export const fetchApplications = createAsyncThunk(
  'onboarding/fetchApplications',
  async (params: { page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const response = await OnboardingService.listApplications(
        params.page,
        params.limit
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取申请列表失败');
    }
  }
);

export const checkApplicationStatus = createAsyncThunk(
  'onboarding/checkStatus',
  async (applicationId: string, { rejectWithValue }) => {
    try {
      const response = await OnboardingService.checkStatus(applicationId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '检查状态失败');
    }
  }
);

export const fetchAvailablePlugins = createAsyncThunk(
  'onboarding/fetchPlugins',
  async (_, { rejectWithValue }) => {
    try {
      const response = await OnboardingService.getAvailablePlugins();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '获取插件列表失败');
    }
  }
);

interface OnboardingState {
  form: OnboardingForm | null;
  applications: {
    data: OnboardingApplication[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      current_page?: number;
      per_page?: number;
    } | null;
    loading: boolean;
  };
  plugins: any[];
  currentApplication: OnboardingApplication | null;
  lastResult: OnboardingResult | null;
  submitting: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: OnboardingState = {
  form: null,
  applications: {
    data: [],
    pagination: null,
    loading: false,
  },
  plugins: [],
  currentApplication: null,
  lastResult: null,
  submitting: false,
  loading: false,
  error: null,
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentApplication: (state, action: PayloadAction<OnboardingApplication | null>) => {
      state.currentApplication = action.payload;
    },
    clearLastResult: (state) => {
      state.lastResult = null;
    },
    resetForm: (state) => {
      state.form = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // 获取入驻表单
    builder
      .addCase(fetchOnboardingForm.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOnboardingForm.fulfilled, (state, action: PayloadAction<OnboardingForm>) => {
        state.loading = false;
        state.form = action.payload;
      })
      .addCase(fetchOnboardingForm.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // 提交申请
    builder
      .addCase(submitApplication.pending, (state) => {
        state.submitting = true;
        state.error = null;
        state.lastResult = null;
      })
      .addCase(submitApplication.fulfilled, (state, action: PayloadAction<OnboardingResult>) => {
        state.submitting = false;
        state.lastResult = action.payload;
      })
      .addCase(submitApplication.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });

    // 获取申请列表
    builder
      .addCase(fetchApplications.pending, (state) => {
        state.applications.loading = true;
        state.error = null;
      })
      .addCase(fetchApplications.fulfilled, (state, action) => {
        state.applications.loading = false;
        state.applications.data = action.payload.data || [];
        state.applications.pagination = action.payload.pagination || null;
      })
      .addCase(fetchApplications.rejected, (state, action) => {
        state.applications.loading = false;
        state.error = action.payload as string;
      });

    // 检查申请状态
    builder
      .addCase(checkApplicationStatus.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkApplicationStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(checkApplicationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // 获取可用插件
    builder
      .addCase(fetchAvailablePlugins.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAvailablePlugins.fulfilled, (state, action) => {
        state.loading = false;
        state.plugins = action.payload;
      })
      .addCase(fetchAvailablePlugins.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentApplication, clearLastResult, resetForm } = onboardingSlice.actions;
export default onboardingSlice.reducer;
